import { useState, JSX } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCreateCheckout } from "../../hooks/useSubscription";
import styles from "./PlanSelection.module.css";
import logo from "../../assets/logo.png";

interface Feature { text: string; included: boolean; }
interface Plan {
  id: string; name: string; subtitle: string;
  monthlyPrice: number; yearlyPrice: number;
  color: "blue" | "orange" | "dark"; badge?: string;
  features: Feature[];
}

const PLANS: Plan[] = [
  {
    id: "starter", name: "Starter Plan", subtitle: "Flights Only",
    monthlyPrice: 99, yearlyPrice: 79, color: "blue",
    features: [
      { text: "Flight Booking Engine connected to your or consolidator GDS", included: true },
      { text: "Home Page", included: true },
      { text: "About Us Page", included: true },
      { text: "Contact Us Page", included: true },
      { text: "Special Offers (Manual Build)", included: true },
      { text: "1 Custom Travel Query Form", included: true },
      { text: "2 x Custom Query Form", included: true },
      { text: "Package Booking Engine", included: false },
      { text: "Choice of one of our existing colour themes or your own custom colour theme", included: false },
    ],
  },
  {
    id: "standard", name: "Standard Plan", subtitle: "Flight and hotel package",
    monthlyPrice: 149, yearlyPrice: 119, color: "orange", badge: "Most Popular",
    features: [
      { text: "Flight Booking Engine connected to your or consolidator GDS", included: true },
      { text: "Home Page", included: true },
      { text: "About Us Page", included: true },
      { text: "Contact Us Page", included: true },
      { text: "Special Offers (Manual Build)", included: true },
      { text: "1 Custom Travel Query Form", included: true },
      { text: "2 x Custom Query Form", included: true },
      { text: "Package Booking Engine", included: true },
      { text: "Choice of one of our existing colour themes or your own custom colour theme", included: true },
    ],
  },
  {
    id: "advanced", name: "Advanced Plan", subtitle: "Fully managed",
    monthlyPrice: 199, yearlyPrice: 159, color: "dark",
    features: [
      { text: "Flight Booking Engine connected to your or consolidator GDS", included: true },
      { text: "Home Page", included: true },
      { text: "About Us Page", included: true },
      { text: "Contact Us Page", included: true },
      { text: "Special Offers (Manual Build)", included: true },
      { text: "1 Custom Travel Query Form", included: true },
      { text: "2 x Custom Query Form", included: true },
      { text: "Package Booking Engine", included: true },
      { text: "Choice of one of our existing colour themes or your own custom colour theme", included: true },
    ],
  },
];

function CheckIcon(): JSX.Element { return (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg>); }
function CrossIcon(): JSX.Element { return (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>); }

const PlanSelection = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { execute: createCheckout, isLoading, error: apiError } = useCreateCheckout();

  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [selectedPlan, setSelectedPlan] = useState<string>("standard");
  const [errorMsg, setErrorMsg] = useState<string | null>((location.state as any)?.error ?? null);

  const activePlan = PLANS.find((p) => p.id === selectedPlan)!;
  const activePrice = billing === "monthly" ? activePlan.monthlyPrice : activePlan.yearlyPrice;

  const handleContinue = async () => {
    setErrorMsg(null);
    const result = await createCheckout(activePlan.id, billing);
    if (result) {
      window.location.href = result.checkoutUrl;
    } else if (apiError) {
      setErrorMsg(apiError);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.blobBlue} />
      <div className={styles.blobOrange} />
      <div className={styles.logo}><img src={logo} alt="FlyGDS" className={styles.logoImg} /><span className={styles.stepBadge}>Step 2 of 4</span></div>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Choose your plan</h1>
          <p className={styles.subtitle}>Select the plan that best fits your agency. You can upgrade anytime.</p>
        </div>
        {errorMsg && <div style={{ background: "#FEF2F2", color: "#DC2626", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{errorMsg}</div>}
        <div className={styles.toggleRow}>
          <span className={`${styles.toggleLabel} ${billing === "monthly" ? styles.toggleLabelActive : ""}`}>Build Monthly</span>
          <button className={styles.toggle} onClick={() => setBilling((b) => (b === "monthly" ? "yearly" : "monthly"))} aria-label="Toggle billing"><span className={`${styles.toggleThumb} ${billing === "yearly" ? styles.toggleThumbRight : ""}`} /></button>
          <span className={`${styles.toggleLabel} ${billing === "yearly" ? styles.toggleLabelActive : ""}`}>Build Yearly<span className={styles.saveBadge}>Save 20%</span></span>
        </div>
        <div className={styles.plansGrid}>
          {PLANS.map((plan, idx) => {
            const price = billing === "monthly" ? plan.monthlyPrice : plan.yearlyPrice;
            const isActive = selectedPlan === plan.id;
            return (
              <div key={plan.id} className={[styles.planCard, styles[`planCard_${plan.color}`], isActive ? styles.planCardSelected : ""].join(" ")} style={{ animationDelay: `${idx * 0.08}s` }} onClick={() => setSelectedPlan(plan.id)}>
                {plan.badge && <div className={styles.popularBadge}>{plan.badge}</div>}
                <div className={styles.planHeader}>
                  <div className={styles.planNameRow}><div className={`${styles.planDot} ${styles[`planDot_${plan.color}`]}`} /><div><span className={styles.planName}>{plan.name}</span><span className={styles.planSubtitle}>{plan.subtitle}</span></div></div>
                  <div className={styles.priceRow}><span className={styles.currency}>$</span><span className={styles.price}>{price}</span><span className={styles.period}>/ Month</span></div>
                </div>
                <div className={styles.divider} />
                <ul className={styles.featureList}>
                  {plan.features.map((f, i) => (<li key={i} className={`${styles.featureItem} ${!f.included ? styles.featureItemMuted : ""}`}><span className={`${styles.featureIcon} ${f.included ? styles.featureIconYes : styles.featureIconNo}`}>{f.included ? <CheckIcon /> : <CrossIcon />}</span><span className={styles.featureText}>{f.text}</span></li>))}
                </ul>
                <div className={`${styles.selectIndicator} ${isActive ? styles[`selectActive_${plan.color}`] : ""}`}>
                  {isActive ? (<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg>Selected</>) : "Select Plan"}
                </div>
              </div>
            );
          })}
        </div>
        <p className={styles.footNote}>🔒 Secured by Stripe · Cancel anytime · No hidden fees</p>
      </div>
      <div className={styles.ctaBar}>
        <div className={styles.ctaSummary}>
          <span className={styles.ctaLabel}>Selected:</span>
          <span className={styles.ctaPlanName}>{activePlan.name}</span>
          <span className={styles.ctaPrice}>${activePrice}/mo</span>
          <span className={styles.ctaBilling}>{billing === "yearly" ? "· Billed yearly" : "· Billed monthly"}</span>
        </div>
        <button className={styles.ctaBtn} onClick={handleContinue} disabled={isLoading}>
          {isLoading ? (<span className={styles.btnSpinner} />) : (<><span>Continue to Payment</span><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12,5 19,12 12,19" /></svg></>)}
        </button>
      </div>
    </div>
  );
};

export default PlanSelection;